import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Grid, Alert, Button, Skeleton, TextField,
    FormControl, InputLabel, Select, MenuItem, IconButton, Tabs, Tab,
    TablePagination, Dialog, DialogContent, DialogTitle, DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Close as CloseIcon,
    VerifiedUser as VerifiedIcon,
    Refresh as RefreshIcon,
    HourglassTop as HourglassIcon,
    ImageNotSupported as ImageOffIcon,
} from '@mui/icons-material';
import { supabase } from '../../api/supabase';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import { adminTokens as T } from '../../theme/adminTokens';

const DRAWER_WIDTH = 260;
const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];

interface VerificationRequest {
    id: string;
    user_id: string;
    pose_type: string;
    verification_photo_url: string;
    status: 'pending' | 'approved' | 'rejected' | 'expired';
    rejection_reason?: string | null;
    created_at: string;
    reviewed_at?: string | null;
    reviewed_by?: string | null;
    user_name: string;
    user_age: number | null;
    user_gender: string;
    user_photos: string[];
}

interface CountsByStatus {
    pending: number;
    approved: number;
    rejected: number;
    expired: number;
    approvedThisWeek: number;
    rejectedThisWeek: number;
}

interface ReviewDialogState {
    open: boolean;
    request: VerificationRequest | null;
    action: 'approve' | 'reject' | null;
}

const REJECTION_REASONS = [
    "Photo doesn't match profile",
    'Pose not clear or incorrect',
    'Face not clearly visible',
    'Photo appears edited or filtered',
    'Inappropriate content',
    'Custom reason',
];

const STATUS_TONE: Record<string, { bg: string; text: string; label: string }> = {
    pending:   { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Pending' },
    approved:  { bg: T.color.success.soft, text: T.color.success.text, label: 'Approved' },
    rejected:  { bg: T.color.error.soft,   text: T.color.error.text,   label: 'Rejected' },
    expired:   { bg: T.color.surface.sunken, text: T.color.text.secondary, label: 'Expired' },
};

const POSE_EMOJI: Record<string, string> = {
    peace_sign: '✌️', thumbs_up: '👍', hand_on_chin: '🤔',
    smile: '😊', wave: '👋', ok_sign: '👌',
};

const formatPoseLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatRelative = (iso: string) => {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const t = STATUS_TONE[status] || STATUS_TONE.pending;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: t.bg, color: t.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        }}>{t.label}</Box>
    );
};

const VERIFICATION_BUCKET = 'verification-photos';
const PUBLIC_PREFIX = `/storage/v1/object/public/${VERIFICATION_BUCKET}/`;

/** Storage path from a stored verification photo URL (the app saves the public URL). */
const verificationPhotoPath = (stored: string | null): string | null => {
    if (!stored) return null;
    const i = stored.indexOf(PUBLIC_PREFIX);
    if (i >= 0) return decodeURIComponent(stored.slice(i + PUBLIC_PREFIX.length).split('?')[0]);
    return /^https?:/i.test(stored) ? null : stored;
};

/**
 * The bucket is private, so swap each stored URL for a one-hour signed URL. The
 * admin storage policy (profiles.is_admin) is what allows signing every member's photo.
 */
const signVerificationPhotos = async <T extends { verification_photo_url: string }>(rows: T[]): Promise<T[]> => {
    const paths = Array.from(new Set(rows.map(r => verificationPhotoPath(r.verification_photo_url)).filter(Boolean) as string[]));
    if (paths.length === 0) return rows;
    const { data, error } = await supabase.storage.from(VERIFICATION_BUCKET).createSignedUrls(paths, 60 * 60);
    if (error || !data) return rows;
    const signed = new Map(data.filter(d => d.signedUrl).map(d => [d.path, d.signedUrl]));
    return rows.map(r => {
        const path = verificationPhotoPath(r.verification_photo_url);
        return { ...r, verification_photo_url: (path && signed.get(path)) || r.verification_photo_url };
    });
};

const SafeImage: React.FC<{ src: string; alt: string; sx?: any; rounded?: number }> = ({ src, alt, sx, rounded = T.radius.md }) => {
    const [failed, setFailed] = useState(false);
    if (failed || !src) {
        return (
            <Box sx={{
                ...sx, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                bgcolor: T.color.surface.sunken,
                color: T.color.text.tertiary,
                borderRadius: `${rounded}px`,
                fontSize: T.type.size.xs,
                gap: `${T.space[1]}px`,
            }}>
                <ImageOffIcon sx={{ fontSize: 20 }} />
                Image unavailable
            </Box>
        );
    }
    return (
        <Box component="img" src={src} alt={alt}
            onError={() => setFailed(true)}
            sx={{ ...sx, borderRadius: `${rounded}px`, objectFit: 'cover' }}
        />
    );
};

const VerificationReviewScreen: React.FC = () => {
    const [requests, setRequests] = useState<VerificationRequest[]>([]);
    const [counts, setCounts] = useState<CountsByStatus | null>(null);
    const [countsLoading, setCountsLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [statusFilter, setStatusFilter] = useState<string>('pending');

    const [reviewDialog, setReviewDialog] = useState<ReviewDialogState>({ open: false, request: null, action: null });
    const [selectedRejectionReason, setSelectedRejectionReason] = useState('');
    const [customRejectionReason, setCustomRejectionReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchCounts = async () => {
        try {
            setCountsLoading(true);
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
            const [pending, approved, rejected, expired, approvedThisWeek, rejectedThisWeek] = await Promise.all([
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'rejected'),
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'expired'),
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'approved').gte('reviewed_at', weekAgo),
                supabase.from('profile_verification_requests').select('id', { count: 'exact', head: true }).eq('status', 'rejected').gte('reviewed_at', weekAgo),
            ]);
            setCounts({
                pending: pending.count ?? 0,
                approved: approved.count ?? 0,
                rejected: rejected.count ?? 0,
                expired: expired.count ?? 0,
                approvedThisWeek: approvedThisWeek.count ?? 0,
                rejectedThisWeek: rejectedThisWeek.count ?? 0,
            });
        } catch {
            setCounts(null);
        } finally {
            setCountsLoading(false);
        }
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            let query = supabase
                .from('profile_verification_requests')
                .select(`*, profiles!profile_verification_requests_user_id_fkey ( full_name, birth_date, gender, photos )`, { count: 'exact' });
            if (statusFilter !== 'all') query = query.eq('status', statusFilter);
            const start = page * rowsPerPage;
            const end = start + rowsPerPage - 1;
            query = query.range(start, end).order('created_at', { ascending: false });
            const { data, error: fetchError, count } = await query;
            if (fetchError) throw fetchError;

            const transformed: VerificationRequest[] = (data || []).map((req: any) => {
                const profile = req.profiles || {};
                const birthDate = profile.birth_date ? new Date(profile.birth_date) : null;
                const age = birthDate ? new Date().getFullYear() - birthDate.getFullYear() : null;
                let userPhotos: string[] = [];
                if (Array.isArray(profile.photos)) {
                    userPhotos = profile.photos
                        .map((p: any) => typeof p === 'string' ? p : (p?.uri || p?.url))
                        .filter(Boolean);
                }
                return {
                    id: req.id,
                    user_id: req.user_id,
                    pose_type: req.pose_type,
                    verification_photo_url: req.verification_photo_url,
                    status: req.status,
                    rejection_reason: req.rejection_reason,
                    created_at: req.created_at,
                    reviewed_at: req.reviewed_at,
                    reviewed_by: req.reviewed_by,
                    user_name: profile.full_name || 'Unknown',
                    user_age: age,
                    user_gender: profile.gender || 'Not specified',
                    user_photos: userPhotos,
                };
            });
            setRequests(await signVerificationPhotos(transformed));
            setTotalCount(count || 0);
        } catch (err: any) {
            setError(err.message || 'Failed to load verification requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCounts(); }, []);
    useEffect(() => { fetchRequests(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, rowsPerPage, statusFilter]);

    const handleStatusTabChange = (_: React.SyntheticEvent, value: string) => {
        setStatusFilter(value);
        setPage(0);
    };

    const openReview = (request: VerificationRequest, action: 'approve' | 'reject') => {
        setReviewDialog({ open: true, request, action });
        setSelectedRejectionReason('');
        setCustomRejectionReason('');
    };
    const closeReview = () => {
        setReviewDialog({ open: false, request: null, action: null });
        setSelectedRejectionReason('');
        setCustomRejectionReason('');
    };

    const handleSubmitReview = async () => {
        if (!reviewDialog.request || !reviewDialog.action) return;
        if (reviewDialog.action === 'reject') {
            if (!selectedRejectionReason) {
                setError('Please select a rejection reason.');
                return;
            }
            if (selectedRejectionReason === 'Custom reason' && !customRejectionReason.trim()) {
                setError('Please describe the custom reason.');
                return;
            }
        }
        setSubmitting(true);
        try {
            const reason = reviewDialog.action === 'reject'
                ? (selectedRejectionReason === 'Custom reason' ? customRejectionReason : selectedRejectionReason)
                : null;
            const { error: rpcError } = await supabase.rpc('review_verification_request', {
                p_request_id: reviewDialog.request.id,
                p_action: reviewDialog.action,
                p_rejection_reason: reason,
            });
            if (rpcError) throw rpcError;
            await Promise.all([fetchRequests(), fetchCounts()]);
            closeReview();
        } catch (err: any) {
            setError(err.message || `Failed to ${reviewDialog.action} verification`);
        } finally {
            setSubmitting(false);
        }
    };

    const avgReviewSummary = useMemo(() => {
        if (!counts) return null;
        const reviewedThisWeek = counts.approvedThisWeek + counts.rejectedThisWeek;
        if (reviewedThisWeek === 0) return null;
        return `${reviewedThisWeek} reviewed this week`;
    }, [counts]);

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar pendingReportsCount={0} />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Header */}
                <Box sx={{ mb: `${T.space[6]}px`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: `${T.space[3]}px` }}>
                    <Box>
                        <Typography sx={{
                            fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                            color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                        }}>Verification review</Typography>
                        <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                            Approve or reject user photo verifications by comparing the pose photo against their profile.
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => { fetchCounts(); fetchRequests(); }}
                        disabled={loading || countsLoading}
                        aria-label="Refresh"
                        sx={{
                            color: T.color.text.secondary,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.md}px`,
                        }}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* KPI row */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Pending review"
                            value={counts?.pending}
                            icon={<HourglassIcon />}
                            tone={(counts?.pending ?? 0) > 20 ? 'error' : 'warning'}
                            loading={countsLoading}
                            onClick={() => { setStatusFilter('pending'); setPage(0); }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Approved (7d)"
                            value={counts?.approvedThisWeek}
                            icon={<ApproveIcon />}
                            tone="success"
                            loading={countsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Rejected (7d)"
                            value={counts?.rejectedThisWeek}
                            icon={<RejectIcon />}
                            tone="error"
                            loading={countsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Total verified"
                            value={counts?.approved}
                            icon={<VerifiedIcon />}
                            tone="info"
                            loading={countsLoading}
                        />
                    </Grid>
                </Grid>

                {/* Tabs */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    mb: `${T.space[4]}px`,
                }}>
                    <Tabs
                        value={statusFilter}
                        onChange={handleStatusTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            px: `${T.space[3]}px`,
                            minHeight: 48,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: T.type.size.sm,
                                fontWeight: T.type.weight.semibold,
                                color: T.color.text.secondary,
                                minHeight: 48,
                            },
                            '& .Mui-selected': { color: `${T.color.brand[600]} !important` },
                            '& .MuiTabs-indicator': { backgroundColor: T.color.brand[500] },
                        }}
                    >
                        <Tab label={`Pending${counts ? ` · ${counts.pending}` : ''}`} value="pending" />
                        <Tab label={`Approved${counts ? ` · ${counts.approved}` : ''}`} value="approved" />
                        <Tab label={`Rejected${counts ? ` · ${counts.rejected}` : ''}`} value="rejected" />
                        <Tab label={`Expired${counts ? ` · ${counts.expired}` : ''}`} value="expired" />
                        <Tab label="All" value="all" />
                    </Tabs>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: `${T.space[4]}px`, borderRadius: `${T.radius.md}px` }}
                        onClose={() => setError(null)}
                    >{error}</Alert>
                )}

                {/* Card grid */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid item xs={12} sm={6} lg={4} key={i}>
                                <Skeleton variant="rectangular" height={380} sx={{ borderRadius: `${T.radius.lg}px` }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : requests.length === 0 ? (
                    <Box sx={{
                        bgcolor: T.color.surface.raised,
                        border: `1px solid ${T.color.surface.border}`,
                        borderRadius: `${T.radius.lg}px`,
                        boxShadow: T.shadow.sm,
                        py: `${T.space[7]}px`, textAlign: 'center',
                        color: T.color.text.tertiary,
                    }}>
                        <VerifiedIcon sx={{ fontSize: 48, color: T.color.text.tertiary, mb: `${T.space[2]}px` }} />
                        <Typography sx={{ fontSize: T.type.size.md, fontWeight: T.type.weight.semibold, color: T.color.text.secondary }}>
                            {statusFilter === 'pending' ? 'All caught up — no pending verifications.' : 'Nothing in this category.'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {requests.map(req => (
                                <Grid item xs={12} sm={6} lg={4} key={req.id}>
                                    <Box sx={{
                                        bgcolor: T.color.surface.raised,
                                        border: `1px solid ${T.color.surface.border}`,
                                        borderRadius: `${T.radius.lg}px`,
                                        boxShadow: T.shadow.sm,
                                        overflow: 'hidden',
                                        display: 'flex', flexDirection: 'column',
                                        transition: `box-shadow ${T.motion.fast}, transform ${T.motion.fast}`,
                                        '&:hover': { boxShadow: T.shadow.md, transform: 'translateY(-1px)' },
                                        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
                                    }}>
                                        {/* Header strip */}
                                        <Box sx={{ p: `${T.space[3]}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <StatusBadge status={req.status} />
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
                                                px: `${T.space[2]}px`, py: `2px`,
                                                bgcolor: T.color.brand[50],
                                                color: T.color.brand[600],
                                                borderRadius: `${T.radius.pill}px`,
                                                fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                            }}>
                                                <span aria-hidden>{POSE_EMOJI[req.pose_type] || '📸'}</span>
                                                {formatPoseLabel(req.pose_type)}
                                            </Box>
                                        </Box>

                                        {/* Verification photo */}
                                        <SafeImage
                                            src={req.verification_photo_url}
                                            alt={`Verification by ${req.user_name}`}
                                            sx={{ width: `calc(100% - ${T.space[3] * 2}px)`, height: 260, mx: `${T.space[3]}px`, mb: `${T.space[3]}px` }}
                                        />

                                        {/* Identity + age */}
                                        <Box sx={{ px: `${T.space[4]}px`, mb: `${T.space[3]}px` }}>
                                            <Typography sx={{
                                                fontSize: T.type.size.md, fontWeight: T.type.weight.semibold,
                                                color: T.color.text.primary,
                                            }}>
                                                {req.user_name}{req.user_age ? `, ${req.user_age}` : ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>
                                                {req.user_gender} · submitted {formatRelative(req.created_at)}
                                            </Typography>
                                        </Box>

                                        {/* Profile photos thumbnails */}
                                        <Box sx={{ px: `${T.space[4]}px`, mb: `${T.space[3]}px` }}>
                                            <Typography sx={{
                                                fontSize: T.type.size.xs, color: T.color.text.tertiary,
                                                textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: T.type.weight.semibold,
                                                mb: `${T.space[1]}px`,
                                            }}>Profile photos</Typography>
                                            {req.user_photos.length > 0 ? (
                                                <Box sx={{ display: 'flex', gap: `${T.space[1]}px`, overflowX: 'auto' }}>
                                                    {req.user_photos.slice(0, 4).map((url, i) => (
                                                        <SafeImage
                                                            key={i}
                                                            src={url}
                                                            alt={`Profile ${i + 1}`}
                                                            sx={{ width: 56, height: 56, flexShrink: 0 }}
                                                            rounded={T.radius.sm}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>
                                                    No profile photos uploaded.
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Rejection reason if rejected */}
                                        {req.status === 'rejected' && req.rejection_reason && (
                                            <Box sx={{
                                                mx: `${T.space[4]}px`, mb: `${T.space[3]}px`,
                                                p: `${T.space[2]}px`,
                                                bgcolor: T.color.error.soft, color: T.color.error.text,
                                                borderRadius: `${T.radius.md}px`,
                                                fontSize: T.type.size.xs,
                                            }}>
                                                <strong>Rejected:</strong> {req.rejection_reason}
                                            </Box>
                                        )}

                                        {/* Actions */}
                                        <Box sx={{ mt: 'auto', p: `${T.space[3]}px`, borderTop: `1px solid ${T.color.surface.border}`, display: 'flex', gap: `${T.space[2]}px` }}>
                                            {req.status === 'pending' ? (
                                                <>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        startIcon={<ApproveIcon />}
                                                        onClick={() => openReview(req, 'approve')}
                                                        sx={{
                                                            bgcolor: T.color.success.base,
                                                            textTransform: 'none',
                                                            fontWeight: T.type.weight.semibold,
                                                            '&:hover': { bgcolor: T.color.success.text },
                                                        }}
                                                    >Approve</Button>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        startIcon={<RejectIcon />}
                                                        onClick={() => openReview(req, 'reject')}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: T.type.weight.semibold,
                                                            color: T.color.error.text,
                                                            borderColor: T.color.error.soft,
                                                            '&:hover': { borderColor: T.color.error.base, bgcolor: T.color.error.soft },
                                                        }}
                                                    >Reject</Button>
                                                </>
                                            ) : (
                                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, mx: 'auto' }}>
                                                    Reviewed {formatDate(req.reviewed_at)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{
                            mt: `${T.space[4]}px`,
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.lg}px`,
                            boxShadow: T.shadow.sm,
                        }}>
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            />
                        </Box>
                    </>
                )}

                {avgReviewSummary && (
                    <Typography sx={{ mt: `${T.space[3]}px`, fontSize: T.type.size.xs, color: T.color.text.tertiary, textAlign: 'right' }}>
                        {avgReviewSummary}
                    </Typography>
                )}
            </Box>

            {/* Side-by-side review dialog */}
            <Dialog
                open={reviewDialog.open}
                onClose={closeReview}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: `${T.radius.lg}px` } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: `${T.space[2]}px` }}>
                    <Box>
                        <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                            {reviewDialog.action === 'approve' ? 'Approve verification' : 'Reject verification'}
                        </Typography>
                        {reviewDialog.request && (
                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary }}>
                                {reviewDialog.request.user_name}{reviewDialog.request.user_age ? `, ${reviewDialog.request.user_age}` : ''} · {POSE_EMOJI[reviewDialog.request.pose_type] || '📸'} {formatPoseLabel(reviewDialog.request.pose_type)}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={closeReview} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: `${T.space[5]}px` }}>
                    {reviewDialog.request && (
                        <>
                            <Grid container spacing={3} sx={{ mb: `${T.space[4]}px` }}>
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{
                                        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                        color: T.color.text.secondary, textTransform: 'uppercase', letterSpacing: 0.4,
                                        mb: `${T.space[2]}px`,
                                    }}>Verification photo</Typography>
                                    <SafeImage
                                        src={reviewDialog.request.verification_photo_url}
                                        alt="Verification photo"
                                        sx={{ width: '100%', height: 360 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{
                                        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                        color: T.color.text.secondary, textTransform: 'uppercase', letterSpacing: 0.4,
                                        mb: `${T.space[2]}px`,
                                    }}>Profile photos</Typography>
                                    {reviewDialog.request.user_photos.length > 0 ? (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: `${T.space[2]}px` }}>
                                            {reviewDialog.request.user_photos.slice(0, 4).map((url, i) => (
                                                <SafeImage
                                                    key={i}
                                                    src={url}
                                                    alt={`Profile ${i + 1}`}
                                                    sx={{ width: '100%', height: 175 }}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: T.color.surface.sunken, color: T.color.text.tertiary,
                                            fontSize: T.type.size.sm, borderRadius: `${T.radius.md}px`,
                                        }}>No profile photos uploaded.</Box>
                                    )}
                                </Grid>
                            </Grid>

                            {reviewDialog.action === 'reject' && (
                                <Box>
                                    <FormControl fullWidth size="small" sx={{ mb: `${T.space[3]}px` }}>
                                        <InputLabel>Rejection reason</InputLabel>
                                        <Select
                                            value={selectedRejectionReason}
                                            label="Rejection reason"
                                            onChange={(e) => setSelectedRejectionReason(e.target.value)}
                                        >
                                            {REJECTION_REASONS.map((r) => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedRejectionReason === 'Custom reason' && (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Describe the issue"
                                            value={customRejectionReason}
                                            onChange={(e) => setCustomRejectionReason(e.target.value)}
                                            placeholder="What specifically is wrong with this verification photo?"
                                        />
                                    )}
                                </Box>
                            )}
                            {reviewDialog.action === 'approve' && (
                                <Alert severity="success" sx={{ borderRadius: `${T.radius.md}px` }}>
                                    Approving will mark this user as verified across the app.
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: `${T.space[3]}px` }}>
                    <Button onClick={closeReview} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        onClick={handleSubmitReview}
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            textTransform: 'none',
                            fontWeight: T.type.weight.semibold,
                            bgcolor: reviewDialog.action === 'approve' ? T.color.success.base : T.color.error.base,
                            '&:hover': {
                                bgcolor: reviewDialog.action === 'approve' ? T.color.success.text : T.color.error.text,
                            },
                        }}
                    >
                        {submitting ? 'Submitting…' : reviewDialog.action === 'approve' ? 'Approve verification' : 'Reject verification'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VerificationReviewScreen;
